// src/pages/VacationRequests/MyVacationRequests.jsx

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { buildPublicFileUrl } from '../../config/env';
import { getRoleApiPrefix } from '../../utils/roleCapabilities';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  TextField,
  Stack,
  Tooltip,
  Link,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import PublicIcon from '@mui/icons-material/Public';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import dayjs from 'dayjs';

/* ---------------------- helpers ---------------------- */
const fmtDate = (d) => (d ? dayjs(d).format('MMM D, YYYY') : '—');

const EMPTY_VALUE = 'N/A';

function hasValue(value) {
  return value !== null && value !== undefined && String(value).trim() !== '';
}

function displayValue(value, fallback = EMPTY_VALUE) {
  return hasValue(value) ? value : fallback;
}

function fmtDateTime(value) {
  if (!hasValue(value)) return EMPTY_VALUE;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('MMM D, YYYY h:mm A') : EMPTY_VALUE;
}

function getAttachmentPath(request) {
  return (
    request?.attachment_url ||
    request?.attachment ||
    request?.file_url ||
    request?.file_path ||
    request?.document_path ||
    request?.document
  );
}

function getAttachmentName(path) {
  if (!hasValue(path)) return 'Attachment';
  const cleanPath = String(path).split('?')[0].split('#')[0];
  const filename = cleanPath.split(/[\\/]/).pop();
  try {
    return decodeURIComponent(filename || 'Attachment');
  } catch {
    return filename || 'Attachment';
  }
}

function getApproverName(request) {
  // Prefer the HR approver (HR Admin or HR Coordinator who finalized) over the
  // manager-level approver, so the person who actually approved is shown.
  return request?.hr_approver?.name
    || request?.approver?.name
    || request?.approved_by_name
    || request?.approved_by
    || EMPTY_VALUE;
}

function DetailItem({ label, value, wide = false }) {
  return (
    <Box sx={{ minWidth: 0, gridColumn: wide ? '1 / -1' : undefined }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 800 }}>
        {label}
      </Typography>
      {React.isValidElement(value) ? (
        <Box sx={{ mt: 0.45 }}>{value}</Box>
      ) : (
        <Typography variant="body2" sx={{ mt: 0.25, fontWeight: 700, overflowWrap: 'anywhere' }}>
          {value}
        </Typography>
      )}
    </Box>
  );
}

function DetailSection({ title, children }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.25,
        borderRadius: 2,
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Typography variant="subtitle2" sx={{ mb: 1.2, fontWeight: 900 }}>
        {title}
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
          gap: 1,
        }}
      >
        {children}
      </Box>
    </Paper>
  );
}

function getRequestedDays(start_date, end_date) {
  if (!start_date || !end_date) return 0;
  const s = dayjs(start_date);
  const e = dayjs(end_date);
  if (!s.isValid() || !e.isValid() || e.isBefore(s)) return 0;
  return e.diff(s, 'day') + 1; // inclusive
}

function StatusChip({ value }) {
  const v = (value || 'Pending').toLowerCase();
  const map = {
    approved: { color: 'success', icon: <CheckCircleOutlineIcon sx={{ fontSize: 16 }} /> },
    rejected: { color: 'error', icon: <CancelOutlinedIcon sx={{ fontSize: 16 }} /> },
    cancelled: { color: 'warning', icon: <CancelOutlinedIcon sx={{ fontSize: 16 }} /> },
    pending: { color: 'info', icon: <HourglassBottomIcon sx={{ fontSize: 16 }} /> },
  };
  const { color, icon } = map[v] || { color: 'default', icon: <HourglassBottomIcon sx={{ fontSize: 16 }} /> };
  return <Chip size="small" color={color} variant="outlined" icon={icon} label={value || 'Pending'} />;
}

/* ---------------------- component ---------------------- */
function MyVacationRequests() {
  const theme = useTheme();
  const downSm = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  // cancel flow
  const [openCancel, setOpenCancel] = useState(false);
  const [cancelId, setCancelId] = useState(null);
  const [processing, setProcessing] = useState(false);

  // details modal
  const [openModal, setOpenModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);

  // UX
  const [error, setError] = useState('');
  const [okMsg, setOkMsg] = useState('');
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  // from storage
  const role = localStorage.getItem('role');

  useEffect(() => {
    fetchMyRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const apiPrefix = getRoleApiPrefix(role);
  const getApiUrl = () => `${apiPrefix}/Myindex/vacation-requests`;
  const getApiUrlForCancel = () => `${apiPrefix}/vacation-requests/${cancelId}`;

  const fetchMyRequests = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get(getApiUrl());
      setRequests(res.data || []);
    } catch (err) {
      console.error('Failed to fetch vacation requests:', err);
      setError('Could not load your vacation requests. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleClickCancel = (requestId) => {
    setCancelId(requestId);
    setOpenCancel(true);
  };

  const confirmCancel = async () => {
    if (!cancelId) return;
    setProcessing(true);
    try {
      await api.patch(getApiUrlForCancel(), { status: 'Cancelled' });
      setOkMsg('Request cancelled successfully.');
      setOpenCancel(false);
      setCancelId(null);
      await fetchMyRequests();
    } catch (err) {
      console.error('Failed to cancel request:', err);
      setError('Could not cancel the request. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const closeCancel = () => {
    if (processing) return;
    setOpenCancel(false);
    setCancelId(null);
  };

  const handleNewRequest = () => navigate('/requests/new');


  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    setOpenModal(true);
  };
  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedRequest(null);
  };

  const stats = useMemo(() => {
    const base = { total: requests.length, pending: 0, approved: 0, rejected: 0, cancelled: 0 };
    for (const r of requests) {
      const s = (r?.status || 'Pending').toLowerCase();
      if (s.includes('approve')) base.approved += 1;
      else if (s.includes('reject')) base.rejected += 1;
      else if (s.includes('cancel')) base.cancelled += 1;
      else base.pending += 1;
    }
    return base;
  }, [requests]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (requests || []).filter((r) => {
      const name = r?.user?.name?.toLowerCase() || '';
      const leave = r?.leave_type?.toLowerCase() || '';
      const country = r?.country?.toLowerCase() || '';
      const matchesQuery = !q || name.includes(q) || leave.includes(q) || country.includes(q);
      const matchesStatus =
        statusFilter === 'All' || (r?.status || 'Pending').toLowerCase() === statusFilter.toLowerCase();
      return matchesQuery && matchesStatus;
    });
  }, [requests, query, statusFilter]);

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', mt: 6 }}>
        <CircularProgress />
        <Typography sx={{ mt: 1 }} color="text.secondary">
          Loading your vacation requests…
        </Typography>
      </Box>
    );
  }

  const headerGrad = `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${
    theme.palette.secondary2?.main || '#2389ba'
  } 100%)`;

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          background:
            theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : theme.palette.background.paper,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            background: headerGrad,
            color: '#fff',
            py: { xs: 3, md: 4 },
            px: { xs: 2, md: 4 },
            borderRadius: 2,
            mb: 3,
            boxShadow: '0 8px 28px rgba(0,0,0,0.18)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: 'space-between',
              gap: 1.5,
              mb: 2,
            }}
          >
            <Typography variant={downSm ? 'h5' : 'h4'} sx={{ fontWeight: 700 }}>
              My Vacation Requests
            </Typography>
            <Stack direction="row" spacing={1}>
              <Tooltip title="Refresh">
                <span>
                  <Button
                    onClick={fetchMyRequests}
                    startIcon={<RefreshIcon />}
                    variant="outlined"
                    sx={{
                      color: '#fff',
                      borderColor: alpha('#fff', 0.6),
                      '&:hover': { borderColor: '#fff', background: alpha('#000', 0.1) },
                    }}
                  >
                    Refresh
                  </Button>
                </span>
              </Tooltip>
              <Button
                onClick={handleNewRequest}
                startIcon={<AddCircleOutlineIcon />}
                variant="contained"
                sx={{ bgcolor: '#fff', color: theme.palette.primary.main, fontWeight: 700 }}
              >
                New Request
              </Button>
            </Stack>
          </Box>

          {/* Quick Stats */}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip size="small" icon={<FlightTakeoffIcon sx={{ fontSize: 16 }} />} label={`Total: ${stats.total}`} />
            <Chip
              size="small"
              color="info"
              icon={<HourglassBottomIcon sx={{ fontSize: 16 }} />}
              label={`Pending: ${stats.pending}`}
            />
            <Chip
              size="small"
              color="success"
              icon={<CheckCircleOutlineIcon sx={{ fontSize: 16 }} />}
              label={`Approved: ${stats.approved}`}
            />
            <Chip
              size="small"
              color="error"
              icon={<CancelOutlinedIcon sx={{ fontSize: 16 }} />}
              label={`Rejected: ${stats.rejected}`}
            />
            <Chip
              size="small"
              color="warning"
              icon={<CancelOutlinedIcon sx={{ fontSize: 16 }} />}
              label={`Cancelled: ${stats.cancelled}`}
            />
          </Stack>
        </Box>

        {/* Toolbar */}
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'stretch', md: 'center' }}
          justifyContent="space-between"
          sx={{
            mb: 2,
            p: 1.25,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            background:
              theme.palette.mode === 'dark'
                ? alpha('#fff', 0.03)
                : alpha(theme.palette.primary.main, 0.03),
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
            <SearchIcon sx={{ opacity: 0.6 }} />
            <TextField
              size="small"
              fullWidth
              placeholder="Search by name, leave type, or country…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </Stack>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="status-filter-label">Filter status</InputLabel>
            <Select
              labelId="status-filter-label"
              label="Filter status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              {['All', 'Pending', 'Approved', 'Rejected', 'Cancelled'].map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Table / Empty state */}
        {filtered.length === 0 ? (
          <Paper
            variant="outlined"
            sx={{
              p: { xs: 3, md: 5 },
              textAlign: 'center',
              borderRadius: 3,
              background:
                theme.palette.mode === 'dark'
                  ? alpha('#fff', 0.02)
                  : alpha(theme.palette.primary.light, 0.06),
            }}
          >
            <FlightTakeoffIcon sx={{ fontSize: 40, opacity: 0.6 }} />
            <Typography sx={{ mt: 1 }} variant="h6">
              No vacation requests
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
              Try changing your filters or create a new request.
            </Typography>
            <Button startIcon={<AddCircleOutlineIcon />} onClick={handleNewRequest} variant="contained">
              New Request
            </Button>
          </Paper>
        ) : (
          <TableContainer
            sx={{
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              maxHeight: 560,
            }}
          >
            <Table stickyHeader size="small" aria-label="My vacation requests">
              <TableHead>
                <TableRow
                  sx={{
                    '& th': {
                      backgroundColor:
                        theme.palette.mode === 'dark'
                          ? alpha('#fff', 0.03)
                          : alpha(theme.palette.primary.main, 0.05),
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      fontWeight: 700,
                    },
                  }}
                >
                  <TableCell>Name</TableCell>
                  <TableCell>Leave Type</TableCell>
                  <TableCell>Start Date</TableCell>
                  <TableCell>End Date</TableCell>
                  <TableCell>Days</TableCell>
                  <TableCell>Country</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {filtered.map((req) => {
                  const days = getRequestedDays(req.start_date, req.end_date);
                  const canCancel = (req.status || '').toLowerCase() === 'pending';
                  return (
                    <TableRow
                      key={req.id}
                      hover
                      sx={{
                        transition: 'background .15s ease',
                        '&:nth-of-type(odd)': {
                          backgroundColor:
                            theme.palette.mode === 'dark'
                              ? alpha('#fff', 0.01)
                              : alpha(theme.palette.primary.light, 0.02),
                        },
                      }}
                    >
                      <TableCell>{req.user?.name || '—'}</TableCell>
                      <TableCell>{req.leave_type || '—'}</TableCell>
                      <TableCell>{fmtDate(req.start_date)}</TableCell>
                      <TableCell>{fmtDate(req.end_date)}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          variant="outlined"
                          icon={<CalendarMonthIcon sx={{ fontSize: 16 }} />}
                          label={days ? `${days} day${days > 1 ? 's' : ''}` : '—'}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          variant="outlined"
                          icon={<PublicIcon sx={{ fontSize: 16 }} />}
                          label={req.country || 'N/A'}
                        />
                      </TableCell>
                      <TableCell><StatusChip value={req.status} /></TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Tooltip title="View details">
                            <span>
                              <Button variant="outlined" size="small" onClick={() => handleViewDetails(req)}>
                                View
                              </Button>
                            </span>
                          </Tooltip>
                          {canCancel && (
                            <Tooltip title="Cancel request">
                              <span>
                                <Button
                                  variant="contained"
                                  color="warning"
                                  size="small"
                                  disabled={processing}
                                  onClick={() => handleClickCancel(req.id)}
                                >
                                  Cancel
                                </Button>
                              </span>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* ---------- Details Modal ---------- */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}
      >
        <DialogTitle sx={{ pb: 1.25 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} justifyContent="space-between">
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 900 }}>
                Leave Request Details
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Full vacation request record, review status, contact details, and supporting attachment.
              </Typography>
            </Box>
            {selectedRequest ? <StatusChip value={selectedRequest.status} /> : null}
          </Stack>
        </DialogTitle>
        {selectedRequest && (
          <DialogContent dividers sx={{ bgcolor: alpha(theme.palette.primary.main, 0.025) }}>
            {(() => {
              const requestedDays =
                selectedRequest.days_requested ||
                selectedRequest.days ||
                getRequestedDays(selectedRequest.start_date, selectedRequest.end_date);
              const attachmentPath = getAttachmentPath(selectedRequest);
              const attachmentUrl = attachmentPath ? buildPublicFileUrl(attachmentPath) : null;

              return (
                <Stack spacing={1.5}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      borderColor: alpha(theme.palette.primary.main, 0.18),
                    }}
                  >
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1}
                      alignItems={{ xs: 'flex-start', sm: 'center' }}
                      justifyContent="space-between"
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 900 }}>
                          Request #{displayValue(selectedRequest.id || selectedRequest.request_id)}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 900, overflowWrap: 'anywhere' }}>
                          {displayValue(selectedRequest.user?.name, 'Unknown employee')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {displayValue(selectedRequest.leave_type)} leave from {fmtDate(selectedRequest.start_date)} to {fmtDate(selectedRequest.end_date)}
                        </Typography>
                      </Box>
                      <Chip
                        variant="outlined"
                        icon={<CalendarMonthIcon sx={{ fontSize: 16 }} />}
                        label={requestedDays ? `${requestedDays} day${requestedDays > 1 ? 's' : ''}` : EMPTY_VALUE}
                        sx={{ borderRadius: 2, fontWeight: 800 }}
                      />
                    </Stack>
                  </Paper>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' },
                      gap: 1.2,
                    }}
                  >
                  <DetailSection title="Request information">
                    <DetailItem label="Leave type" value={displayValue(selectedRequest.leave_type)} />
                    <DetailItem label="Requested days" value={requestedDays || EMPTY_VALUE} />
                    <DetailItem label="Start date" value={fmtDate(selectedRequest.start_date)} />
                    <DetailItem label="End date" value={fmtDate(selectedRequest.end_date)} />
                    <DetailItem label="Manager status" value={<StatusChip value={selectedRequest.status} />} />
                    <DetailItem label="HR status" value={<StatusChip value={selectedRequest.hr_status || 'Pending'} />} />
                  </DetailSection>

                  <DetailSection title="Employee and travel details">
                    <DetailItem label="Employee" value={displayValue(selectedRequest.user?.name, 'Unknown employee')} />
                    <DetailItem label="Employee code" value={displayValue(selectedRequest.user_id || selectedRequest.user?.employee_code)} />
                    <DetailItem label="Country" value={displayValue(selectedRequest.country)} />
                    <DetailItem label="Phone" value={displayValue(selectedRequest.phone_number)} />
                    <DetailItem label="Location in country" value={displayValue(selectedRequest.location_in_country)} wide />
                  </DetailSection>

                  <DetailSection title="Review history">
                    <DetailItem label="Submitted" value={fmtDateTime(selectedRequest.created_at)} />
                    <DetailItem label="Last updated" value={fmtDateTime(selectedRequest.updated_at)} />
                    <DetailItem label="Approved by" value={getApproverName(selectedRequest)} />
                    <DetailItem label="Approved at" value={fmtDateTime(selectedRequest.approved_at)} />
                    <DetailItem label="Finalized at" value={fmtDateTime(selectedRequest.finalized_at)} />
                    <DetailItem label="Current state" value={displayValue(selectedRequest.hr_status || selectedRequest.status)} />
                  </DetailSection>

                  <DetailSection title="Notes and attachment">
                    <DetailItem
                      label="Description"
                      value={displayValue(selectedRequest.description, 'No description provided')}
                      wide
                    />
                    <DetailItem
                      label="Attachment"
                      wide
                      value={
                        attachmentUrl ? (
                          <Link href={attachmentUrl} target="_blank" rel="noopener noreferrer" underline="hover">
                            {getAttachmentName(attachmentPath)}
                          </Link>
                        ) : (
                          'No attachment uploaded'
                        )
                      }
                    />
                  </DetailSection>
                  </Box>
                </Stack>
              );
            })()}
            <Stack spacing={1} sx={{ display: 'none' }}>
              <Typography variant="body1"><strong>Name:</strong> {selectedRequest.user?.name || '—'}</Typography>
              <Typography variant="body1"><strong>Leave Type:</strong> {selectedRequest.leave_type || '—'}</Typography>
              <Typography variant="body1">
                <strong>Start:</strong> {fmtDate(selectedRequest.start_date)} &nbsp; / &nbsp;
                <strong>End:</strong> {fmtDate(selectedRequest.end_date)}
              </Typography>
              <Typography variant="body1">
                <strong>Days:</strong> {getRequestedDays(selectedRequest.start_date, selectedRequest.end_date) || '—'}
              </Typography>
              <Typography variant="body1"><strong>Country:</strong> {selectedRequest.country || 'N/A'}</Typography>
              <Typography variant="body1"><strong>Phone:</strong> {selectedRequest.phone_number || 'N/A'}</Typography>
              <Typography variant="body1"><strong>Location in Country:</strong> {selectedRequest.location_in_country || 'N/A'}</Typography>
              <Typography variant="body1"><strong>Description:</strong> {selectedRequest.description || 'No description'}</Typography>
              <Typography variant="body1"><strong>Status:</strong> {selectedRequest.status || '—'}</Typography>
            </Stack>
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={handleCloseModal} autoFocus>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ---------- Cancel Confirm Dialog ---------- */}
      <Dialog open={openCancel} onClose={closeCancel} fullWidth maxWidth="xs">
        <DialogTitle>Confirm Cancellation</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ mb: 1.5 }}>
            Are you sure you want to cancel this request?
          </Typography>
          <Alert severity="warning" variant="outlined">
            This action cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCancel} disabled={processing}>No, go back</Button>
          <Button variant="contained" color="warning" onClick={confirmCancel} disabled={processing}>
            {processing ? 'Cancelling…' : 'Yes, cancel it'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------- Snackbars ---------- */}
      <Snackbar
        open={!!okMsg}
        autoHideDuration={3000}
        onClose={() => setOkMsg('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setOkMsg('')} variant="filled">
          {okMsg}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={4000}
        onClose={() => setError('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setError('')} variant="filled">
          {error}
        </Alert>
      </Snackbar>
    </>
  );
}

export default MyVacationRequests;
